import React, { useState } from 'react';
import {
    Box,
    Stepper,
    Step,
    StepLabel,
    Button,
    Typography,
    TextField,
    Paper,
    Container,
    Checkbox,
    FormControlLabel,
    Alert,
    Grid,
    Card,
    CardContent,
    CardActions,
    CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { onboardingApi } from '../api/onboarding';
import { customersApi } from '../api/customers';
import { useUser } from '../contexts/UserContext';
import { useAuth } from '../hooks/useAuth';

import { LegalModal } from '../components/LegalModal';
import { BAA_TEXT, HIPAA_POLICY_TEXT, TERMS_OF_SERVICE_TEXT, BILLING_POLICY_TEXT } from '../assets/LegalText';

const steps = ['Customer/Company Profile', 'Legal Agreements', 'Select Plan', 'Finalize'];

// Plans are now fetched dynamically from the backend
interface Plan {
    id: string;
    name: string;
    price: string;
    description: string;
    features: string[];
}
export const OnboardingWizard: React.FC = () => {
    const [activeStep, setActiveStep] = useState(0);
    // Use global user context instead of local fetch to avoid double-firing and 429s
    const { status, isLoading: isUserLoading, refreshStatus } = useUser();
    const { logout } = useAuth();

    const [profile, setProfile] = useState({
        legalName: '',
        displayName: '',
        organizationName: '',
        organizationWebsite: '',
        organizationSupportEmail: '',
        billingEmail: '', // NEW: Billing Email
        taxId: '',
        // NEW: Split Address Fields
        addressLine1: '',
        addressLine2: '', city: '',
        state: '',
        zip: '',
        country: 'USA',
        phone: '',
        phoneCountryCode: '+1', // NEW: Phone Country Code
        isStaff: true, // Defaulting enrolling owner to also be staff/admin
        isCustomerAdmin: true,
    });
    const [agreements, setAgreements] = useState({
        baa: false,
        hipaa: false,
        financial: false, // For Owners
        user: false,      // For Staff/Admins
    });
    const [intendedRole, setIntendedRole] = useState<'owner' | 'employee' | null>(null);
    const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
    const [wantsBulk, setWantsBulk] = useState(false);
    const [dismissedBulk, setDismissedBulk] = useState(false);
    const [stripeLoading, setStripeLoading] = useState(false);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [plansLoading, setPlansLoading] = useState(false);

    // Legal Modal State
    const [legalModalOpen, setLegalModalOpen] = useState(false);
    const [legalModalContent, setLegalModalContent] = useState<{ title: string, content: string }>({ title: '', content: '' });

    const openLegalModal = (title: string, content: string) => {
        setLegalModalContent({ title, content });
        setLegalModalOpen(true);
    };
    const [error, setError] = useState<string | null>(null);
    const [provisioningKey, setProvisioningKey] = useState<string>('');
    const [loginIntent, setLoginIntent] = useState<string | null>(null);
    const [pendingInvitations, setPendingInvitations] = useState<any[] | null>(null);
    const [invitationsLoading, setInvitationsLoading] = useState(false);
    const navigate = useNavigate();

    // Fetch dynamic plans when reaching the Select Plan step
    React.useEffect(() => {
        if (activeStep === 2 && intendedRole === 'owner' && plans.length === 0) {
            const fetchPlans = async () => {
                setPlansLoading(true);
                try {
                    const data = await onboardingApi.getPlans();
                    setPlans(data);
                } catch (err) {
                    console.error("Failed to fetch plans", err);
                    setError("Failed to load subscription plans.");
                } finally {
                    setPlansLoading(false);
                }
            };
            fetchPlans();
        }
    }, [activeStep, intendedRole, plans.length]);

    // Redirect if already complete (but not if they arrived with intent)
    React.useEffect(() => {
        if (sessionStorage.getItem('onboarding_intent') || intendedRole || loginIntent) return;

        if (status?.is_complete) {
            navigate('/');
        }
    }, [status, navigate, intendedRole, loginIntent]);

    // Pickup intent from Login page
    React.useEffect(() => {
        const intent = sessionStorage.getItem('onboarding_intent');
        if (intent === 'enroll' && !intendedRole) {
            setIntendedRole('owner');
            setLoginIntent('enroll');
            sessionStorage.removeItem('onboarding_intent');
        } else if (intent === 'join' && !intendedRole) {
            setIntendedRole('employee');
            setLoginIntent('join');
            sessionStorage.removeItem('onboarding_intent');
        } else if (intent === 'app_login') {
            setLoginIntent('app_login');
            sessionStorage.removeItem('onboarding_intent');
        }

        // Autofill billing email if available and not yet set
        if (status?.email && !profile.billingEmail) {
            setProfile(prev => ({ ...prev, billingEmail: status.email || '' }));
        }
    }, [intendedRole, status?.email]);

    // Button 2 + Active: check if there are pending invitations from other customers
    React.useEffect(() => {
        if (intendedRole === 'employee' && status?.onboarding_status === 'Active') {
            const checkInvitations = async () => {
                setInvitationsLoading(true);
                try {
                    const all = await onboardingApi.listInvitations();
                    const pending = all.filter((inv: any) => inv.status === 'Pending' && inv.type === 'Invitation');
                    setPendingInvitations(pending);
                } catch (err) {
                    console.error('Failed to check invitations', err);
                    setPendingInvitations([]);
                } finally {
                    setInvitationsLoading(false);
                }
            };
            checkInvitations();
        }
    }, [intendedRole, status?.onboarding_status]);

    // NEW: Fetch existing organization setting if they are a returning Admin placed in onboarding
    React.useEffect(() => {
        const fetchExistingProfile = async () => {
            if (intendedRole === 'owner' && status?.customer_id && !profile.legalName) {
                try {
                    const orgSettings = await customersApi.getOrganizationSettings();
                    if (orgSettings) {
                        setProfile(prev => ({
                            ...prev,
                            legalName: orgSettings.legal_name || prev.legalName,
                            displayName: orgSettings.display_name || prev.displayName,
                            organizationName: orgSettings.organization_name || prev.organizationName,
                            organizationWebsite: orgSettings.organization_website || prev.organizationWebsite,
                            organizationSupportEmail: orgSettings.organization_support_email || prev.organizationSupportEmail,
                            billingEmail: orgSettings.billing_email || prev.billingEmail,
                            taxId: orgSettings.tax_id || prev.taxId,
                            addressLine1: orgSettings.address_line1 || prev.addressLine1,
                            addressLine2: orgSettings.address_line2 || prev.addressLine2,
                            city: orgSettings.city || prev.city,
                            state: orgSettings.state || prev.state,
                            zip: orgSettings.zip_code || prev.zip,
                            country: orgSettings.country || prev.country,
                            phone: orgSettings.phone_number || prev.phone,
                            phoneCountryCode: orgSettings.phone_country_code || prev.phoneCountryCode
                        }));
                    }
                } catch (error) {
                    console.error("Failed to fetch existing organization settings during onboarding", error);
                }
            }
        };
        fetchExistingProfile();
    }, [intendedRole, status?.customer_id]);

    // Employee flow no longer fetches invitations by email. They must explicitly provide the provisioning key.

    const handleNext = async () => {
        try {
            if (activeStep === 0) {
                if (intendedRole === 'owner') {
                    // If this is an owner with no customer_id, we enroll them first
                    if (!status?.customer_id) {
                        await onboardingApi.enroll();
                        await refreshStatus();
                    }

                    // Submit profile partial
                    await onboardingApi.updateProfile({
                        customer_id: status?.customer_id, // NEW: Include ID
                        legal_name: profile.legalName,
                        display_name: profile.displayName || profile.legalName,  // NEW: Include Alias
                        organization_name: profile.organizationName,
                        organization_website: profile.organizationWebsite,
                        organization_support_email: profile.organizationSupportEmail,
                        billing_email: profile.billingEmail || status?.email,
                        tax_id: profile.taxId,
                        address_line1: profile.addressLine1,
                        address_line2: profile.addressLine2,
                        city: profile.city,
                        state: profile.state,
                        zip_code: profile.zip,
                        country: profile.country,
                        phone_number: profile.phone,
                        phone_country_code: profile.phoneCountryCode,
                        is_owner_admin: true,
                        is_staff: profile.isStaff,
                        is_customer_admin: profile.isCustomerAdmin,
                    } as any);
                } else if (intendedRole === 'employee' && provisioningKey) {
                    // For staff joining an org, submit the key directly
                    const res = await onboardingApi.redeemInvitation(provisioningKey.trim());
                    await refreshStatus();

                    if (res.status === 'Active') {
                        // User already completed onboarding for this key
                        setActiveStep(steps.length - 1);
                        return;
                    }
                }
            } else if (activeStep === 1) {
                // Record individual signs in the vault
                const customerId = status?.customer_id;
                if (!customerId) throw new Error("No organization context found for signing.");



                const staffId = status?.staff_id;

                if (intendedRole === 'employee') {
                    await onboardingApi.signDocument('StaffAgreement', customerId, staffId);
                    await onboardingApi.signDocument('CompanyBAA', customerId, staffId);
                    await onboardingApi.signDocument('HIPAA', customerId, staffId);

                    await onboardingApi.activateStaffContext();
                    await refreshStatus();
                    // Go to a success step or just the final step
                    setActiveStep(steps.length - 1); // Skip straight to the final success screen for Employees
                    return;
                } else {
                    await onboardingApi.signDocument('HIPAA', customerId, staffId);
                    await onboardingApi.signDocument('CompanyBAA', customerId, staffId);
                    await onboardingApi.signDocument('FinancialResponsibility', customerId, staffId);
                    await onboardingApi.signDocument('UserAgreement', customerId, staffId);
                }
            } else if (activeStep === 2) {
                // Checkout process
                if (!selectedPlan) return;

                setStripeLoading(true);
                try {
                    if (wantsBulk) {
                        const { checkout_url } = await onboardingApi.createBulkCheckout(
                            window.location.origin + '/subscription-success',
                            window.location.origin + '/onboarding'
                        );
                        window.location.href = checkout_url;
                    } else {
                        const { checkout_url } = await onboardingApi.createCheckoutSession(
                            selectedPlan,
                            window.location.origin + '/subscription-success',
                            window.location.origin + '/onboarding'
                        );
                        window.location.href = checkout_url;
                    }
                    return; // Redirecting
                } catch (err: any) {
                    setStripeLoading(false);
                    const errorMsg = err.response?.data?.detail || '';
                    if (errorMsg.includes('already has an active subscription')) {
                        // User already has an active subscription, so let's bypass setup and mark them active
                        try {
                            await onboardingApi.activate();
                            await refreshStatus();
                            navigate('/');
                            return;
                        } catch (activationErr) {
                            throw err; // if activation fails, throw the original error
                        }
                    }
                    throw err;
                }
            } else if (activeStep === steps.length - 1) {
                // Complete onboarding
                navigate('/');
                return;
            }
            setActiveStep((prev) => prev + 1);
            setError(null);
        } catch (err: any) {
            setError(err.message || 'Onboarding step failed');
        }
    };

    const handleBack = () => setActiveStep((prev) => prev - 1);

    const isNextDisabled = () => {
        if (activeStep === 0) {
            if (intendedRole === 'employee') return !provisioningKey;
            return !profile.legalName || !profile.addressLine1 || !profile.city || !profile.state || !profile.zip || !profile.phone;
        }
        if (activeStep === 1) {
            const basic = !(agreements.baa && agreements.hipaa && agreements.user);
            if (status?.is_owner_admin || intendedRole === 'owner') return basic || !agreements.financial;
            if (intendedRole === 'employee') return basic; // Employees must sign BAA, HIPAA, and Staff Agreement
            return basic;
        }
        if (activeStep === 2) return selectedPlan === null;
        return false; // Default to not disabled for other steps
    };

    if (isUserLoading) return <Typography align="center" sx={{ mt: 10 }}>Loading setup...</Typography>;

    // Button 3 + Not Active: tell user to onboard via Button 2
    if (loginIntent === 'app_login' && status && status.onboarding_status !== 'Active') {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Paper sx={{ p: 6, borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="h5" color="warning.main" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Onboarding Required
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                        Your account has not completed onboarding yet.
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        Please log out and use <strong>Button 2</strong> ("Join Organization") on the login page to complete your setup first.
                    </Typography>
                    <Button variant="contained" color="primary" sx={{ mt: 4 }} onClick={() => logout()}>
                        Log Out
                    </Button>
                </Paper>
            </Container>
        );
    }

    // Button 2 + Active + checking invitations: show spinner while loading
    if (intendedRole === 'employee' && status?.onboarding_status === 'Active' && invitationsLoading) {
        return <Typography align="center" sx={{ mt: 10 }}>Checking for pending invitations...</Typography>;
    }

    // Button 2 + Active + no pending invitations: tell user to use Button 3
    if (intendedRole === 'employee' && status?.onboarding_status === 'Active' && pendingInvitations !== null && pendingInvitations.length === 0) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Paper sx={{ p: 6, borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="h5" color="success.main" gutterBottom sx={{ fontWeight: 'bold' }}>
                        All Caught Up!
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                        You have completed all pending onboarding invitations. There are no new invitations at this time.
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        To start processing facesheets, please log out and sign in using <strong>Button 3</strong> ("Login to FaceSheetApp") on the login page.
                    </Typography>
                    <Button variant="contained" color="primary" sx={{ mt: 4 }} onClick={() => logout()}>
                        Log Out
                    </Button>
                </Paper>
            </Container>
        );
    }

    // NEW: Identity Verification for fresh sign-ups (no roles assigned yet)
    const hasNoRoles = status && !status.is_owner_admin && !status.is_customer_admin && !status.is_staff;

    if (status?.onboarding_status === 'Authenticated' && hasNoRoles && !intendedRole) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Paper sx={{ p: 6, borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Before we begin...
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        How do you intend to use FaceSheetApp today?
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid size={12}>
                            <Button
                                fullWidth variant="outlined" size="large" sx={{ py: 2 }}
                                onClick={() => setIntendedRole('owner')}
                            >
                                <Box>
                                    <Typography sx={{ fontWeight: 'bold' }}>I am a Business Owner / Freelancer</Typography>
                                    <Typography variant="caption" sx={{ display: 'block', textTransform: 'none' }}>
                                        I am setting up a NEW billing account for myself or my organization.
                                    </Typography>
                                </Box>
                            </Button>
                        </Grid>
                        <Grid size={12}>
                            <Button
                                fullWidth variant="outlined" size="large" sx={{ py: 2 }}
                                onClick={() => setIntendedRole('employee')}
                            >
                                <Box>
                                    <Typography sx={{ fontWeight: 'bold' }}>I am an Employee / Staff Member</Typography>
                                    <Typography variant="caption" sx={{ display: 'block', textTransform: 'none' }}>
                                        I am joining an existing company or team.
                                    </Typography>
                                </Box>
                            </Button>
                        </Grid>
                    </Grid>

                    <Button variant="text" sx={{ mt: 4 }} onClick={() => navigate('/login')}>
                        Back to Login
                    </Button>
                </Paper>
            </Container>
        );
    }

    // Employee flow no longer requires an invitation pre-check;
    // they fall through to Step 0 to enter their Provisioning Key directly.

    return (
        <Container maxWidth="md" sx={{ mt: 8 }}>
            <Paper sx={{ p: 4, borderRadius: 2 }}>
                <Typography variant="h4" gutterBottom align="center">
                    FaceSheetApp Onboarding
                </Typography>
                <Typography variant="subtitle1" gutterBottom align="center" color="textSecondary">
                    A SaaS product by City Billing, LLC
                </Typography>

                <Stepper activeStep={activeStep} sx={{ py: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Box sx={{ minHeight: 400 }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    {/* Step-status banner — tells returning users exactly what is pending */}
                    {status && !status.is_complete && intendedRole === 'owner' && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {!status.customer_id
                                ? 'Step 1 of 4: Fill in your company profile to get started.'
                                : status.onboarding_status === 'Onboarding'
                                    ? 'Your company profile is saved. Complete the legal agreements and select a subscription plan to activate your account.'
                                    : 'Almost there — review any remaining steps below and click Finalize to complete your account setup.'
                            }
                        </Alert>
                    )}
                    {status && !status.is_complete && intendedRole === 'employee' && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Enter the provisioning key your organization admin provided to join your workspace.
                        </Alert>
                    )}

                    {activeStep === 0 && intendedRole === 'employee' && (
                        <Box sx={{ mt: 4, mb: 4 }}>
                            <Typography variant="h6" gutterBottom>Join Your Organization</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                To join your organization's environment securely, please enter the Provisioning & Billing Key provided by your Administrator.
                            </Typography>

                            <TextField
                                fullWidth
                                label="Provisioning & Billing Key"
                                variant="outlined"
                                margin="normal"
                                value={provisioningKey}
                                onChange={(e) => setProvisioningKey(e.target.value)}
                                helperText="Format: P<YYMMDD><RANDOM> e.g., P260220ABCD"
                            />
                        </Box>
                    )}

                    {activeStep === 0 && intendedRole === 'owner' && (
                        <Box component="form" sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom>Customer/Company Profile</Typography>
                            <TextField
                                fullWidth label="Legal Name (Organization or Personal)"
                                margin="normal" value={profile.legalName}
                                onChange={(e) => setProfile({ ...profile, legalName: e.target.value })}
                            />
                            <TextField
                                fullWidth label="Display Name / Alias (Optional)"
                                margin="normal" value={profile.displayName}
                                onChange={(e) => setProfile({ ...profile, displayName: e.target.value })}
                                helperText="How you want to be known in the system"
                            />
                            <TextField
                                fullWidth label="Organization Name (Public-facing)"
                                margin="normal" value={profile.organizationName}
                                onChange={(e) => setProfile({ ...profile, organizationName: e.target.value })}
                            />
                            <TextField
                                fullWidth label="Organization Website"
                                margin="normal" value={profile.organizationWebsite}
                                onChange={(e) => setProfile({ ...profile, organizationWebsite: e.target.value })}
                            />
                            <TextField
                                fullWidth label="Organization Support Email"
                                margin="normal" value={profile.organizationSupportEmail}
                                onChange={(e) => setProfile({ ...profile, organizationSupportEmail: e.target.value })}
                            />
                            <TextField
                                fullWidth label="Billing Email"
                                margin="normal" value={profile.billingEmail}
                                onChange={(e) => setProfile({ ...profile, billingEmail: e.target.value })}
                                helperText="We will send invoices to this address"
                            />
                            <TextField
                                fullWidth label="Tax ID / EIN (Optional)"
                                margin="normal" value={profile.taxId}
                                onChange={(e) => setProfile({ ...profile, taxId: e.target.value })}
                            />

                            <Typography variant="subtitle2" sx={{ mt: 2 }}>Address</Typography>
                            <TextField
                                fullWidth label="Address Line 1"
                                margin="dense" value={profile.addressLine1}
                                onChange={(e) => setProfile({ ...profile, addressLine1: e.target.value })}
                            />
                            <TextField
                                fullWidth label="Address Line 2 (Suite, Unit, etc.)"
                                margin="dense" value={profile.addressLine2}
                                onChange={(e) => setProfile({ ...profile, addressLine2: e.target.value })}
                            />
                            <Grid container spacing={2}>
                                <Grid size={6}>
                                    <TextField
                                        fullWidth label="City"
                                        margin="dense" value={profile.city}
                                        onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <TextField
                                        fullWidth label="State"
                                        margin="dense" value={profile.state}
                                        onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <TextField
                                        fullWidth label="Zip Code"
                                        margin="dense" value={profile.zip}
                                        onChange={(e) => setProfile({ ...profile, zip: e.target.value })}
                                    />
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid size={4}>
                                    <TextField
                                        fullWidth label="Country Code"
                                        margin="normal" value={profile.phoneCountryCode}
                                        onChange={(e) => setProfile({ ...profile, phoneCountryCode: e.target.value })}
                                    />
                                </Grid>
                                <Grid size={8}>
                                    <TextField
                                        fullWidth label="Phone Number"
                                        margin="normal" value={profile.phone}
                                        onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                                    />
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>Your Roles in this Practice</Typography>
                                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                                    As the person enrolling the company, you are the **Owner Admin**. Would you also like the following roles?
                                </Typography>
                                <FormControlLabel
                                    control={<Checkbox checked={profile.isStaff} onChange={(e) => setProfile({ ...profile, isStaff: e.target.checked })} />}
                                    label="Staff Member (Process PDFs / View Medical Records)"
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={profile.isCustomerAdmin} onChange={(e) => setProfile({ ...profile, isCustomerAdmin: e.target.checked })} />}
                                    label="Customer Admin (Manage Billing & Users)"
                                />
                            </Box>
                        </Box>
                    )}

                    {activeStep === 1 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom>Legal & Compliance</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Please review and accept the following legally binding documents.
                            </Typography>

                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={agreements.baa} onChange={(e) => setAgreements({ ...agreements, baa: e.target.checked })} />}
                                        label="I agree to the Business Associate Agreement (BAA)"
                                    />
                                    <Button size="small" onClick={() => openLegalModal('Business Associate Agreement', BAA_TEXT)}>
                                        View BAA
                                    </Button>
                                </Box>
                                <Typography variant="caption" display="block" sx={{ ml: 4 }}>
                                    Between City Billing, LLC and {profile.legalName || 'Your Organization'}
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={agreements.hipaa} onChange={(e) => setAgreements({ ...agreements, hipaa: e.target.checked })} />}
                                        label="I acknowledge the HIPAA Compliance Policy"
                                    />
                                    <Button size="small" onClick={() => openLegalModal('HIPAA Compliance Policy', HIPAA_POLICY_TEXT)}>
                                        View Policy
                                    </Button>
                                </Box>
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={agreements.user} onChange={(e) => setAgreements({ ...agreements, user: e.target.checked })} />}
                                        label={intendedRole === 'employee' ? "I agree to the Staff Agreement" : "I agree to the SaaS User Terms of Service"}
                                    />
                                    <Button size="small" onClick={() => openLegalModal(intendedRole === 'employee' ? 'Staff Agreement' : 'Terms of Service', TERMS_OF_SERVICE_TEXT)}>
                                        View Terms
                                    </Button>
                                </Box>
                            </Box>

                            {/* Financial Responsibility - Owners ONLY */}
                            {(status?.is_owner_admin || intendedRole === 'owner') && (
                                <Box sx={{ mb: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <FormControlLabel
                                            control={<Checkbox checked={agreements.financial} onChange={(e) => setAgreements({ ...agreements, financial: e.target.checked })} />}
                                            label="I accept full Financial Responsibility for this account"
                                        />
                                        <Button size="small" onClick={() => openLegalModal('Billing Policy', BILLING_POLICY_TEXT)}>
                                            View Billing Policy
                                        </Button>
                                    </Box>
                                    <Typography variant="caption" display="block" sx={{ ml: 4, color: 'error.main' }}>
                                        Required for Customer Owners to enable billing.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    {activeStep === 2 && (
                        <Box sx={{ mt: 2 }}>
                            {intendedRole === 'employee' ? null : (
                                /* OWNER FLOW: SELECT PLAN */
                                <>
                                    <Typography variant="h6" gutterBottom>Select Subscription Plan</Typography>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                        Choose the plan that best fits your company's volume. All plans include full access to the AI extraction suite.
                                    </Typography>

                                    {(() => {
                                        const activePlan = plans.find(p => p.id === selectedPlan);
                                        const isFlexPlan = activePlan?.name.toLowerCase().includes('flex') || plans.findIndex(p => p.id === selectedPlan) === 0;

                                        if (selectedPlan && isFlexPlan && !wantsBulk && !dismissedBulk) {
                                            return (
                                                <Alert severity="info" sx={{ mb: 3, border: '1px solid', borderColor: 'primary.main' }}>
                                                    <Typography variant="subtitle2">Recommended Add-on: Bulk Credits</Typography>
                                                    <Typography variant="body2">
                                                        For users on <strong>{activePlan?.name}</strong>, we recommend purchasing bulk credits to lower your effective cost to <strong>$0.50/PDF</strong> (a 66% savings!).
                                                    </Typography>
                                                    <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                                                        <Button
                                                            size="small" variant="contained"
                                                            onClick={() => setWantsBulk(true)}
                                                        >
                                                            Upgrade to Bulk Package (2,000 PDFs)
                                                        </Button>
                                                        <Button
                                                            size="small" variant="outlined" color="inherit"
                                                            onClick={() => setDismissedBulk(true)}
                                                        >
                                                            No thanks, continue with my plan
                                                        </Button>
                                                    </Box>
                                                </Alert>
                                            );
                                        }
                                        return null;
                                    })()}

                                    {wantsBulk && (
                                        <Alert severity="success" sx={{ mb: 3 }}>
                                            You have selected the <strong>Bulk PDF Processing Package</strong> ($1,000 for 2,000 PDFs).
                                            <Button size="small" sx={{ ml: 2 }} onClick={() => setWantsBulk(false)}>Switch back to Subscription</Button>
                                        </Alert>
                                    )}

                                    {plansLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                            <CircularProgress />
                                            <Typography sx={{ ml: 2 }}>Loading plans...</Typography>
                                        </Box>
                                    ) : (
                                        <Grid container spacing={2}>
                                            {plans.map((plan) => (
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={plan.id}>
                                                    <Card
                                                        raised={selectedPlan === plan.id && !wantsBulk}
                                                        sx={{
                                                            height: '100%',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            border: (selectedPlan === plan.id && !wantsBulk) ? '2px solid' : '1px solid',
                                                            borderColor: (selectedPlan === plan.id && !wantsBulk) ? 'primary.main' : 'divider',
                                                            opacity: wantsBulk ? 0.6 : 1,
                                                            cursor: 'pointer',
                                                            transition: '0.3s',
                                                            '&:hover': { transform: wantsBulk ? 'none' : 'translateY(-4px)' }
                                                        }}
                                                        onClick={() => {
                                                            setSelectedPlan(plan.id);
                                                            setWantsBulk(false);
                                                            setDismissedBulk(false); // Reset dismissal on switch
                                                        }}
                                                    >
                                                        <CardContent sx={{ flexGrow: 1 }}>
                                                            <Typography variant="h6" component="div" color="primary">
                                                                {plan.name}
                                                            </Typography>
                                                            <Typography variant="h5" sx={{ my: 1 }}>
                                                                {plan.price}
                                                            </Typography>
                                                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                                                {plan.description}
                                                            </Typography>
                                                            {plan.features.map((feature, i) => (
                                                                <Typography key={i} variant="caption" display="block">
                                                                    • {feature}
                                                                </Typography>
                                                            ))}
                                                        </CardContent>
                                                        <CardActions>
                                                            <Button
                                                                fullWidth
                                                                variant={selectedPlan === plan.id ? "contained" : "outlined"}
                                                                onClick={() => {
                                                                    setSelectedPlan(plan.id);
                                                                    setDismissedBulk(false);
                                                                }}
                                                            >
                                                                {selectedPlan === plan.id ? "Selected" : "Choose Plan"}
                                                            </Button>
                                                        </CardActions>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Box>
                    )}

                    {activeStep === 3 && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="h4" color="primary" gutterBottom>Setup Complete!</Typography>
                            {intendedRole === 'employee' ? (
                                <Box sx={{ p: 4, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0', mt: 3, mb: 3 }}>
                                    <Typography variant="h6" color="success.main" gutterBottom>
                                        You have successfully onboarded with <strong>{status?.customer_name || 'your organization'}</strong>!
                                    </Typography>
                                    <Typography variant="body1">
                                        Your legal documents have been signed and vaulted. Your account is now fully Active.
                                    </Typography>
                                    <Typography variant="body1" sx={{ mt: 2, fontWeight: 'medium' }}>
                                        To begin processing, please click the <strong>Log Out</strong> button below, and then log back in using the <strong>3rd button</strong> on the login page ("Log in as Waitlist or Staff").
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography sx={{ mt: 2 }}>
                                    Your account is being provisioned. You can now access your dashboard.
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    <Button disabled={activeStep === 0} onClick={handleBack}>Back</Button>

                    {/* Hide NEXT button for Step 3, they will navigate via the Go To Dashboard button */}
                    {activeStep < steps.length - 1 && (
                        <Button
                            variant="contained"
                            disabled={isNextDisabled() || stripeLoading}
                            onClick={handleNext}
                        >
                            {stripeLoading ? <CircularProgress size={24} color="inherit" /> : (
                                activeStep === 1 && intendedRole === 'employee' ? 'Sign Documents' : 'Next'
                            )}
                        </Button>
                    )}
                    {activeStep === steps.length - 1 && (
                        intendedRole === 'employee' ? (
                            <Button variant="contained" color="primary" onClick={() => logout()}>
                                Log Out
                            </Button>
                        ) : (
                            <Button variant="contained" onClick={() => navigate('/')}>
                                Go to Dashboard
                            </Button>
                        )
                    )}
                </Box>
            </Paper>
            {/* Legal Modal Component */}
            <LegalModal
                open={legalModalOpen}
                onClose={() => setLegalModalOpen(false)}
                title={legalModalContent.title}
                content={legalModalContent.content}
                onAgree={() => setLegalModalOpen(false)}
            />
        </Container>
    );
};
